<template>
  <v-dialog
    :value="value"
    max-width="1040"
    content-class="subscribe-dialog"
    overlay-color="#000000"
    overlay-opacity="0.82"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-auto-fix</v-icon>
        {{ isAnonymous ? "Your free conversion is used - get 1 more free"
                       : "You're out of free conversions - keep going" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p class="dialog-sub">
          Keep turning AI images into realistic photos. Any pack or plan
          unlocks everything - Extreme realism, 4x output and watermark-free
          downloads.
        </p>

        <!-- Guests: a free account is the next step, not a card. -->
        <v-card v-if="isAnonymous" outlined class="signup-offer mb-6">
          <div class="signup-offer-body d-flex align-center flex-wrap">
            <div class="signup-offer-text">
              <div class="signup-offer-title">
                <v-icon left color="primary">mdi-gift-outline</v-icon>
                Create a free account - get 1 more free conversion
              </div>
              <div class="signup-offer-sub">
                No card needed. Your converted image and gallery stay with
                your account.
              </div>
            </div>
            <v-btn large color="primary" class="signup-offer-cta" @click="goRegister">
              Sign up free
            </v-btn>
          </div>
        </v-card>

        <!-- One-time packs before the subscription pitch. Guests see them
             too - anonymous visitors can't check out (platform rule), so
             their click routes through the one-click sign-up and reopens
             this dialog to finish the purchase. -->
        <template>
          <div class="packs-title">
            One-time packs - no subscription needed
            <span v-if="isAnonymous" class="packs-title-note">
              (free sign-up first - one click with Google)
            </span>
          </div>
          <v-alert v-if="packError" type="error" dense outlined class="mb-3">
            {{ packError }}
          </v-alert>
          <v-row>
            <v-col v-for="(pack, index) in packs" :key="pack.credits" cols="12" sm="6">
              <!-- The whole card buys the pack; the button is a visual
                   affordance (@click.stop keeps one click = one purchase).
                   The small pack is the hero offer: most visitors have one
                   batch of images to fix, not a monthly need. -->
              <v-card
                outlined
                class="pack-card"
                :class="{
                  'pack-card--busy': packLoading !== null,
                  'pack-card--featured': index === 0
                }"
                :loading="packLoading === pack.credits"
                @click="buyPack(pack)"
              >
                <v-chip v-if="pack.first" x-small color="warning" class="pack-flag">
                  First purchase only
                </v-chip>
                <v-chip v-else-if="index === 0" x-small color="primary" class="pack-flag">
                  Best to start
                </v-chip>
                <div class="pack-body d-flex align-center">
                  <div>
                    <div class="pack-price">
                      ≈ {{ pack.photos }} conversions - ${{ packPrice(pack.credits) }}
                    </div>
                    <div class="pack-fine">
                      {{ pack.credits }} credits · never expire ·
                      {{ pack.first
                        ? "one per account" +
                          (firstPackHoursLeft ? " · " + firstPackHoursLeft + "h left" : "")
                        : "no subscription" }}
                    </div>
                    <div class="unlock-line">
                      <v-icon x-small class="unlock-icon">mdi-lock-open-variant</v-icon>
                      Unlocks Extreme realism + 4x upscale
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    depressed
                    class="pack-buy"
                    :disabled="packLoading !== null"
                    @click.stop="buyPack(pack)"
                  >
                    Buy
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
          <div class="packs-divider">or subscribe and save</div>
        </template>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <!-- align="stretch" + d-flex columns: the cards match heights, and
             mt-auto pins every CTA to the same baseline even though the
             conversion-count lines wrap to different heights per plan. -->
        <v-row v-else align="stretch">
          <v-col
            v-for="plan in paidPlans"
            :key="plan.id"
            cols="12"
            sm="6"
            md="3"
            class="d-flex"
          >
            <v-card
              outlined
              class="plan-card d-flex flex-column flex-grow-1"
              :class="{ 'plan-card--featured': isFeatured(plan) }"
            >
              <div class="plan-body text-center d-flex flex-column flex-grow-1">
                <div class="plan-name">{{ planTitle(plan) }}</div>
                <div class="plan-price">
                  ${{ Number(plan.price_monthly).toFixed(2) }}<span class="plan-per">/mo</span>
                </div>
                <div class="plan-count">
                  ≈ {{ highCount(plan) }} High-realism conversions
                </div>
                <div class="plan-count-sub">
                  or ~{{ basicCount(plan) }} Basic · ~{{ extremeCount(plan) }} Extreme
                </div>
                <div class="unlock-line">
                  <v-icon x-small class="unlock-icon">mdi-lock-open-variant</v-icon>
                  Unlocks Extreme realism + 4x upscale
                </div>
                <!-- Wrapper carries BOTH the push-to-bottom (margin-top:auto)
                     and a guaranteed minimum gap (padding-top) - putting
                     both on the button itself would let `auto` win and
                     collapse the spacing on the tallest card. -->
                <div class="plan-cta-wrap">
                  <v-btn
                    block
                    large
                    class="plan-cta"
                    :color="isFeatured(plan) ? 'primary' : undefined"
                    :outlined="!isFeatured(plan)"
                    @click="choosePlan(plan)"
                  >
                    Choose {{ planTitle(plan) }}
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <p class="dialog-fineprint mt-2 mb-0">
          Counts are for typical images - larger images and sizes use more
          of your monthly allowance. Cancel anytime.
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { registerFirst } from "@shared/utils/memberFunnel";
import upgradeOffer from "../mixins/upgradeOffer";

export default {
  name: "SubscribeDialog",
  mixins: [upgradeOffer],
  props: {
    value: { type: Boolean, default: false },
    // A needs_credits upload parked by a 402: when set, pack checkouts
    // return the buyer to the Enhance page, which resumes this image
    // automatically instead of landing them on /billing.
    resumeImageId: { type: Number, default: null }
  },
  watch: {
    value(open) {
      if (open && !this.loaded) this.fetchPlans();
    }
  },
  mounted() {
    // Born open (e.g. the landing's out-of-allowance redirect sets the
    // flag before first render): the value watcher never fires, so fetch
    // here too.
    if (this.value && !this.loaded) this.fetchPlans();
  },
  methods: {
    // Guests can't check out (purchases need an account): route through
    // the one-click sign-up and come back with this dialog open, as a
    // member, to finish the pack purchase.
    buyPack(pack) {
      if (this.isAnonymous) {
        this.$emit("input", false);
        this.$router.push(registerFirst("/?subscribe=1")).catch(() => {});
        return;
      }
      return upgradeOffer.methods.buyPack.call(this, pack);
    },
    // With a parked upload, Stripe sends the buyer back to the Enhance
    // page, which finishes the paid-for conversion automatically.
    checkoutUrls() {
      if (!this.resumeImageId) return null;
      return {
        successUrl: window.location.origin + "/?purchased=" + this.resumeImageId,
        cancelUrl: window.location.origin + "/"
      };
    },
    onNavigate() {
      this.$emit("input", false);
    },
    // After signing up, come back to this page (e.g. the converted image).
    goRegister() {
      this.$emit("input", false);
      this.$router.push(registerFirst(this.$route.fullPath)).catch(() => {});
    }
  }
};
</script>

<!-- Unscoped on purpose: Vuetify renders the overlay OUTSIDE this
     component (as a sibling in the app root), so a scoped rule can never
     reach it. Keyed off this dialog's own content-class via :has(), so it
     applies only while THIS dialog is open and cannot affect other
     dialogs or other apps. -->
<style>
body:has(.v-dialog__content--active .subscribe-dialog) .v-overlay__scrim {
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
</style>

<style scoped>
/* Vuetify zeroes .v-card__text's top padding when it follows a title
   (.v-card__title + .v-card__text), which leaves this sitting flush
   against AppShell's bordered title bar. Space it here rather than
   fighting that rule's specificity. */
.dialog-sub {
  color: var(--text-secondary);
  margin-top: 20px;
}

/* --- Guest signup offer ------------------------------------------- */

.signup-offer {
  border-color: var(--accent, #a855f7);
  border-radius: 12px;
  box-shadow: 0 0 18px rgba(168, 85, 247, 0.22);
}

.signup-offer-body {
  padding: 18px 20px;
  gap: 14px;
}

.signup-offer-text {
  flex: 1 1 320px;
}

.signup-offer-title {
  font-weight: 700;
  font-size: 1.05rem;
}

.signup-offer-sub {
  color: var(--text-secondary);
  font-size: 0.86rem;
  margin-top: 4px;
}

.signup-offer-cta {
  flex: 0 0 auto;
}

/* --- One-time packs ------------------------------------------------ */

.packs-title {
  font-weight: 700;
  margin-bottom: 10px;
}

.packs-title-note {
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.pack-card {
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.pack-card:hover:not(.pack-card--busy) {
  border-color: var(--accent, #a855f7);
  box-shadow: 0 0 16px rgba(168, 85, 247, 0.3);
  transform: translateY(-2px);
}

.pack-card--busy {
  cursor: default;
}

/* The small pack is the anchor offer at this moment (same treatment the
   featured plan card gets below). */
.pack-card--featured {
  position: relative;
  border-color: var(--accent, #a855f7);
  box-shadow: 0 0 16px rgba(168, 85, 247, 0.28);
}

.pack-flag {
  position: absolute;
  top: -10px;
  left: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Vuetify's filled primary on these small cards: keep the label readable. */
.pack-buy {
  min-width: 72px;
  font-weight: 700;
}

.pack-body {
  padding: 14px 16px;
}

.pack-price {
  font-weight: 800;
  font-size: 1.3rem;
}

.pack-fine {
  color: var(--text-secondary);
  font-size: 0.78rem;
}

/* "or subscribe and save" separator between packs and plan cards */
.packs-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 18px 0 14px;
}

.packs-divider::before,
.packs-divider::after {
  content: "";
  flex: 1;
  border-top: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
}

.plan-card {
  height: 100%;
  border-radius: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.plan-body {
  padding: 22px 18px 20px;
}

.plan-card--featured {
  border-color: var(--accent, #a855f7);
  box-shadow: 0 0 18px rgba(168, 85, 247, 0.28);
}

.plan-name {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.01em;
}

.plan-price {
  font-size: 1.9rem;
  font-weight: 800;
  margin-top: 6px;
  line-height: 1.1;
}

.plan-per {
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--text-secondary);
}

.plan-count {
  margin-top: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.plan-count-sub {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  margin-top: 4px;
}

.plan-cta-wrap {
  margin-top: auto;
  padding-top: 18px;
}

.plan-cta {
  font-weight: 700;
}

/* The explicit unlock promise on every paid card: warm amber, matching
   the "Paid" lock chips it answers. */
.unlock-line {
  margin-top: 6px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #fbbf24;
}

.unlock-line .unlock-icon {
  color: #fbbf24 !important;
  margin-right: 2px;
  vertical-align: -1px;
}

.dialog-fineprint {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
}
</style>
